import {DOM} from "../DOM";
import * as M from "materialize-css";
import {PhoneInput} from "../../../../app/controls/Form/components/PhoneInput/PhoneInput";
import {ControlsForm} from "../../../../app/controls/Form/Form";
import {ControlsFormSelectInput} from "../../../../app/controls/Form/components/SelectInput/SelectInput";
import {InputValidator} from "./InputValidator";
import {SelectValidator} from "./SelectValidator";
import {ElementValidatorUtil} from "./ElementValidatorUtil";
import {FormValidatorParams} from "../../definitions/utils/validator/validator/ValidateFormOptions";
import {FormValidatorUtil} from "./FormValidatorUtil";
import {ControlsFormMultiChooseInput} from "../../../../app/controls/Form/components/MultiChooseInput/MultiChooseInput";
import {ControlsFormSalesInfoBoxInput} from "../../../../app/controls/Form/components/SalesInfoBoxInput/SalesInfoBoxInput";
import {ControlsFormDateTimePicker} from "../../../../app/controls/Form/components/DateTimePicker/DateTimePicker";
import {ControlsFormRatingPicker} from "../../../../app/controls/Form/components/RatingPicker/RatingPicker";
import {ControlsFormCheckboxInput} from "../../../../app/controls/Form/components/CheckboxInput/CheckboxInput";

/**
 * Main validator controller class
 */
export class FormValidator {

    /**
     * Process validations of the form
     * Parse rules from inputs and trigger events to validate them
     * @param options - validation extended options
     */
    public static validateForm(options: FormValidatorParams) : void {

        // Init form
        M.updateTextFields();
        ControlsFormSelectInput.Control.initialize(options.form);
        ControlsFormMultiChooseInput.Control.initialize(options.form);
        ControlsFormSalesInfoBoxInput.Control.initialize(options.form);
        ControlsFormDateTimePicker.Control.initialize(options.form);
        ControlsFormCheckboxInput.Control.initialize(options.form);
        PhoneInput.Control.initialize(options.form);
        ControlsFormRatingPicker.Control.initialize(options.form);

        // Clean unused pattern data attributes
        const patternElements = DOM.Parser.getHtmlElementsByQuery('[pattern]', options.form, true);
        for (let index = 0; index < patternElements.length; ++index) {
            patternElements[index].attributes.removeNamedItem('pattern');
        }

        // Validate number specific rules
        const numberInputs = DOM.Parser.getHtmlElementsByQuery("input[type='number']", options.form, true);
        for (let index = 0; index < numberInputs.length; ++index) {
            const numberInput = numberInputs[index] as HTMLInputElement;
            ElementValidatorUtil.triggerElementValidateNumberOnKey(numberInput);
        }

        // Initialize edit form state
        const inputs = FormValidatorUtil.getAllInputs(options);
        const submit = FormValidatorUtil.getFormSubmit(options);
        if (FormValidatorUtil.isEditForm(options) && submit) {
            submit.disabled = true;
            options.form.dataset.inputs = options?.forceInputCount?.toString() ?? inputs.length.toString();
        }

        // Validate other input / textarea rules
        const formDataCurrent =  ControlsForm.Controller.getFormData(options.form);
        for (let index = 0; index < inputs.length; ++index) {

            // Edit form submit state
            const input = inputs[index] as HTMLInputElement;
            if (input.type.toLowerCase() === 'submit') {
                input.disabled = true;
            }

            // Skip select dropdown generated input
            if (input.type.toLowerCase() === 'submit' || input.classList.contains('select-dropdown')) {
                continue;
            }

            // Initialize edit form state
            if (FormValidatorUtil.isEditForm(options) && !input.hasAttribute('data-current')) {
                input.dataset.current = formDataCurrent[input.name] ?? input.value.trim();
            }

            // Novalidate flag skip
            if (input.dataset.novalidate === 'true') {
                input.dataset.valid = "true";
                continue;
            }

            // Set triggers
            if (input.tagName.toLowerCase() === "textarea") {
                FormValidator.validateTextareaTrigger(input, options);
            }
            if (input.tagName.toLowerCase() === "select") {
                FormValidator.validateSelectTrigger(inputs[index] as HTMLSelectElement, options);
            }
            else {
                FormValidator.validateInputTrigger(input, options);
            }

            input.addEventListener("focusin", () => { ElementValidatorUtil.setElementCanDisplayErrors(input) });
        }
    }

    /**
     * Trigger select validations
     * @param element - select input element
     * @param options - form validation options
     * @private
     */
    private static validateSelectTrigger(element: HTMLSelectElement, options: FormValidatorParams) {
        this.processSelectValidations(element, options);
        element.addEventListener("change", () => { this.processSelectValidations(element, options); });
    }

    /**
     * Process validations on select inputs
     * @param element - select input element
     * @param options - form validation options
     * @private
     */
    private static processSelectValidations(element: HTMLSelectElement, options: FormValidatorParams) {

        element.dataset.valid = "true";
        const parentElement = element.parentElement;
        if (parentElement?.classList.contains('select-wrapper')) {
            parentElement.dataset.valid = "true";
        }
        element.dataset.problem = "";
        element.dataset.filled = element.value.trim().length > 0 ? 'yes' : 'no';

        SelectValidator.validateSelect(element);
        FormValidatorUtil.updateFormState(options);
    }

    /**
     * Trigger validations on input
     * @param element - input element
     * @param options - form validation options
     */
    private static validateInputTrigger(element: HTMLInputElement, options: FormValidatorParams) {

        this.processValidations(element, null, options);
        ElementValidatorUtil.initializeShowPasswordButton(element);

        setTimeout(() => {  this.processAutoInit(element); }, 500)
        element.addEventListener("countrychange", () => { this.processValidations(element, null, options); });
        element.addEventListener("keyup", () => { this.processValidations(element, null, options); });
        element.addEventListener("change", () => { this.processValidations(element, null, options); });
        element.addEventListener("update", () => { this.processValidations(element, true, options); });
        element.addEventListener("input", () => { this.processValidations(element, true, options); });
    }

    /**
     * Initialize validation trigger on textarea
     * @param element - input element
     * @param options - form validation options
     */
    private static validateTextareaTrigger(element: HTMLInputElement, options: FormValidatorParams) : void {

        this.processValidations(element, null, options);
        element.addEventListener("input", () => { this.processValidations(element, null, options); });
        element.addEventListener("change", () => { this.processValidations(element, null, options); });
        element.addEventListener("update", () => { this.processValidations(element, true, options); });
    }

    /**
     * If auto-active flag, always show label as active
     * @param element - input element
     * @private
     */
    private static processAutoInit(element: HTMLInputElement) : void {

        if (DOM.Parser.getDataByNameFromElementOrNull('autoActive', element)) {
            if (element.parentElement) {
                const label = DOM.Parser.getElementByTag('label', element.parentElement, true);
                if (label) { label.classList.add('active'); }
            }
        }
    }

    /**
     * Process validations on input or textarea elements
     * @param element - input or textarea
     * @param isUpdate - is just update - don't dispatch any events
     * @param options - form validation options
     * @private
     */
    private static processValidations(element: HTMLInputElement|HTMLTextAreaElement, isUpdate: boolean|null, options: FormValidatorParams) {

        element.dataset.valid = "true";
        element.dataset.problem = "";
        element.dataset.filled = element.value.trim().length > 0 ? 'yes' : 'no';

        // Is disabled
        if (element.disabled) { return; }

        // Is phone input
        if (element.classList.contains('PhoneInput-input-input')) {
            PhoneInput.Helpers.validatePhoneInput(element);
        }

        InputValidator.validateInput(element, isUpdate, options);
        FormValidatorUtil.updateFormState(options);

        const validCallback = !options.validCallbacks ? null : options.validCallbacks.find((item) => {
            return item.inputName === element.name;
        });

        if (!validCallback) { return; }
        if (element.dataset.valid.toString() === 'true') {
            validCallback.callback.valid();
        }
        if (element.dataset.valid.toString() === 'false') {
            validCallback.callback.invalid();
        }
    }
}

